import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATION_CHANNEL, normaliseUiLocale } from '@his/shared';
import { FileText, MessageSquareText } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { LANGUAGE_NAMES, activeProfile, languageOptions } from '@/lib/country';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { CHANNEL_LABELS, TEMPLATES_QUERY_KEY, type Template } from './types';

export const TEMPLATES_RETURN_TO = '/notifications?tab=templates';

export function TemplatePage() {
  const { templateId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), TEMPLATES_RETURN_TO);

  const templates = useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: () => get<Template[]>('/notifications/templates'),
    enabled: Boolean(templateId),
  });
  const template = templates.data?.find((row) => row.id === templateId) ?? null;

  if (templateId && !template) {
    return (
      <FormScreen title="Edit template" icon={MessageSquareText} backTo={returnTo}>
        <RecordState error={templates.error} missing={Boolean(templates.data)} />
      </FormScreen>
    );
  }

  return <TemplateForm key={templateId || 'new'} template={template ?? {}} returnTo={returnTo} />;
}

function TemplateForm({ template, returnTo }: { template: Partial<Template>; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [initial] = useState(() => ({
    code: template.code ?? '',
    channel: template.channel ?? 'sms',
    name: template.name ?? '',
    subject: template.subject ?? '',
    body: template.body ?? '',
    locale: normaliseUiLocale(template.locale) as string,
    isActive: template.isActive ?? true,
  }));
  const [form, setForm] = useState(initial);
  const languages = languageOptions(activeProfile());
  const savedLocale = normaliseUiLocale(template.locale);
  const offered = languages.some((option) => option.value === savedLocale) ? languages : [...languages, { value: savedLocale, label: LANGUAGE_NAMES[savedLocale] }];

  const save = useMutation({
    mutationFn: () =>
      post('/notifications/templates', {
        code: form.code,
        channel: form.channel,
        name: form.name,
        subject: form.subject || undefined,
        body: form.body,
        locale: form.locale,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      toast.success('Template saved');
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.code.trim().length >= 2 && form.name.trim().length >= 2 && form.body.trim().length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={template.id ? 'Edit template' : 'New template'}
      description="Anything in {{double braces}} is filled in when the message goes out"
      icon={MessageSquareText}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={FileText}>
        <Field label="Code" required hint="How the system refers to it">
          <Input
            value={form.code}
            disabled={Boolean(template.id)}
            onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\W+/g, '_') })}
            placeholder="appointment_reminder"
          />
        </Field>
        <Field label="Channel" required>
          <ChoiceButtons
            layout="chips"
            value={form.channel}
            onChange={(channel) => setForm({ ...form, channel })}
            required
            options={NOTIFICATION_CHANNEL.map((channel) => ({ value: channel, label: CHANNEL_LABELS[channel] ?? channel }))}
          />
        </Field>
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Language" hint="Patients who prefer this language get this wording; everyone else gets the English one">
          <ChoiceButtons
            value={form.locale}
            onChange={(locale) => setForm({ ...form, locale })}
            options={offered}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'message')} icon={MessageSquareText}>
        {form.channel === 'email' && (
          <Field label="Subject" className="sm:col-span-2">
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </Field>
        )}
        <Field label="Wording" required className="sm:col-span-2">
          <Textarea
            rows={5}
            dir="auto"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Dear {{patientName}}, your appointment is on {{date}} at {{time}}."
          />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          checked={form.isActive}
          onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
          label="In use"
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Save template" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
